import { t, type AmsMessageKey } from '../../messages.ts';

/**
 * Integrity → Access log: the pure part of the screen, kept out of the .tsx
 * so a test can read it without a DOM. Rows come from
 * GET /v1/platform/access-log; their words are under `integrity.accessLog.`.
 */

export const ACCESS_LOG_KINDS = ['read', 'edit', 'deletion', 'login_as'] as const;
export type AccessLogKind = (typeof ACCESS_LOG_KINDS)[number];
export type AccessLogFilter = 'all' | AccessLogKind;

export interface AccessLogRow {
  readonly id: string;
  readonly kind: AccessLogKind;
  readonly at: string;
  readonly platform_user: { readonly id: string; readonly name: string | null; readonly email: string | null };
  readonly operator_id: string | null;
  readonly operator: string | null;
  /** A read's subject or an audit action. */
  readonly code: string;
  readonly entity_type: string | null;
  readonly entity_id: string | null;
  readonly reason: string | null;
  readonly target: string | null;
  readonly target_kind: string | null;
}

export interface AccessLogPage {
  readonly items: readonly AccessLogRow[];
  readonly next_before: string | null;
}

/** Each kind's name, keyed literally so the catalogue test sees them used. */
const KIND_KEYS: Readonly<Record<AccessLogKind, AmsMessageKey>> = {
  read: 'integrity.accessLog.kind.read',
  edit: 'integrity.accessLog.kind.edit',
  deletion: 'integrity.accessLog.kind.deletion',
  login_as: 'integrity.accessLog.kind.login_as',
};

export function kindLabel(kind: AccessLogKind): string {
  return t(KIND_KEYS[kind]);
}

/** What a cross-tenant read looked at, and what a platform act did, in words. */
const CODE_KEYS: Readonly<Record<string, AmsMessageKey>> = {
  'operator.affiliates': 'integrity.accessLog.code.operator.affiliates',
  'operator.brands': 'integrity.accessLog.code.operator.brands',
  'operator.analytics': 'integrity.accessLog.code.operator.analytics',
  'operator.periods': 'integrity.accessLog.code.operator.periods',
  'operator.referrals': 'integrity.accessLog.code.operator.referrals',
  'operator.team': 'integrity.accessLog.code.operator.team',
  'affiliate.profiles': 'integrity.accessLog.code.affiliate.profiles',
  'affiliate.detail': 'integrity.accessLog.code.affiliate.detail',
  'affiliate.analytics': 'integrity.accessLog.code.affiliate.analytics',
  'affiliate.links': 'integrity.accessLog.code.affiliate.links',
  'affiliate.invoices': 'integrity.accessLog.code.affiliate.invoices',
  'affiliate.disputes': 'integrity.accessLog.code.affiliate.disputes',
  'platform.direct_edit': 'integrity.accessLog.code.platform.direct_edit',
  'operator.soft_delete': 'integrity.accessLog.code.operator.soft_delete',
  'operator.restore': 'integrity.accessLog.code.operator.restore',
  'operator.purge': 'integrity.accessLog.code.operator.purge',
  'brand.soft_delete': 'integrity.accessLog.code.brand.soft_delete',
  'brand.restore': 'integrity.accessLog.code.brand.restore',
  'brand.purge': 'integrity.accessLog.code.brand.purge',
  'affiliate.soft_delete': 'integrity.accessLog.code.affiliate.soft_delete',
  'affiliate.restore': 'integrity.accessLog.code.affiliate.restore',
  'affiliate.purge': 'integrity.accessLog.code.affiliate.purge',
};

const TARGET_KIND_KEYS: Readonly<Record<string, AmsMessageKey>> = {
  OPERATOR_USER: 'integrity.loginAs.kind.OPERATOR_USER',
  BRAND_USER: 'integrity.loginAs.kind.BRAND_USER',
  AFFILIATE: 'integrity.loginAs.kind.AFFILIATE',
};

/**
 * One row as a sentence. A login-as names who; any audit row a direct edit
 * wrote beyond the edit's own record keeps its action code, since the list of
 * what a route may write is open-ended — the code is searchable, a guess is not.
 */
export function accessLogWhat(row: AccessLogRow): string {
  if (row.kind === 'login_as') {
    const kindKey = row.target_kind ? TARGET_KIND_KEYS[row.target_kind] : undefined;
    return t('integrity.accessLog.code.loginAs', {
      target: row.target ?? '—',
      kind: kindKey ? t(kindKey) : (row.target_kind ?? ''),
    });
  }
  const key = CODE_KEYS[row.code];
  if (key) return t(key);
  if (row.kind === 'edit') return t('integrity.accessLog.code.editedRecord', { action: row.code });
  return row.code;
}

/** The platform user as a name; the email only when there is no name. */
export function platformUserName(row: AccessLogRow): string {
  return row.platform_user.name?.trim() || row.platform_user.email || row.platform_user.id;
}

/** The query GET /v1/platform/access-log takes for these filters and page. */
export function accessLogQuery(f: { operatorId: string; kind: AccessLogFilter; before?: string | null }): Record<string, string> {
  return {
    ...(f.operatorId ? { operator_id: f.operatorId } : {}),
    ...(f.kind !== 'all' ? { kind: f.kind } : {}),
    ...(f.before ? { before: f.before } : {}),
  };
}

/** The loaded rows as the export writes them — what the table shows, nothing more. */
export function accessLogExport(rows: readonly AccessLogRow[]): {
  headers: string[];
  keys: string[];
  rows: string[][];
} {
  return {
    headers: [
      t('integrity.accessLog.col.when'), t('integrity.accessLog.col.who'), t('common.email'),
      t('common.operator'), t('integrity.accessLog.col.kind'), t('integrity.accessLog.col.what'),
      t('common.reason'),
    ],
    keys: ['at', 'platform_user', 'email', 'operator', 'kind', 'what', 'reason'],
    rows: rows.map((r) => [
      r.at,
      platformUserName(r),
      r.platform_user.email ?? '',
      r.operator ?? r.operator_id ?? '',
      kindLabel(r.kind),
      accessLogWhat(r),
      r.reason ?? '',
    ]),
  };
}
